import React, { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { ActionModalPresenter } from "./ActionModal.presenter";
import {
  Action,
  ActionCreateRequestPayload,
  ActionUpdateRequestPayload,
} from "@/domain/action.interface";
import {
  useActionCreateOne,
  useActionUpdateOne,
  useAllActions,
} from "@/hooks/useActions";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import validationSchema, { ActionFormData } from "./validation.schema";
import { PermissionDeniedModal } from "@/components/PermissionDeniedModal";
import { ErrorResponse } from "@/domain/error.interface";
import { ActionStatusEnum } from "@/domain/action.enum";

export interface ActionModalContainerProps {
  open: boolean;
  mode: "create" | "edit";
  action?: Action | null;
  onClose: () => void;
  onSuccess?: () => void;
  canAddAction: boolean;
  canEditAction: boolean;
}

export const ActionModalContainer: FC<ActionModalContainerProps> = ({
  open,
  mode,
  action,
  onClose,
  onSuccess,
  canAddAction,
  canEditAction,
}) => {
  const { t } = useTranslation("common");
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const createAuditLog = useCreateAuditlog();

  // Mutations
  const actionCreateOne = useActionCreateOne();
  const actionUpdateOne = useActionUpdateOne();

  // Fetch all actions for subActions dropdown
  const { allActions, isLoadingAllActions } = useAllActions(open);

  // State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPermissionDeniedModalOpen, setIsPermissionDeniedModalOpen] =
    useState(false);

  // Form setup
  const methods = useForm<ActionFormData>({
    resolver: yupResolver(validationSchema(t)),
    mode: "all",
    defaultValues: {
      name: "",
      code: "",
      description: "",
      type: undefined,
      status: ActionStatusEnum.ACTIVE,
      link: "",
      order: 0,
      icon: "",
      subActions: [],
    },
  });

  // Initialize form data when action is loaded (edit mode)
  useEffect(() => {
    if (mode === "edit" && action && open) {
      methods.reset({
        name: action.name,
        code: action.code,
        description: action.description,
        type: action.type,
        status: action.status,
        link: action.link,
        order: action.order,
        icon: action.icon,
        subActions: action.subActions || [],
      });
    } else if (mode === "create" && open) {
      methods.reset({
        name: "",
        code: "",
        description: "",
        type: undefined,
        status: ActionStatusEnum.ACTIVE,
        link: "",
        order: 0,
        icon: "",
        subActions: [],
      });
    }
  }, [mode, action, open, methods]);

  // Handle submit
  const handleSubmit = async () => {
    if (mode === "create" && !canAddAction) {
      showSnackBar(t("generic_not_allowed") || "Not allowed", "error");
      return;
    }
    if (mode === "edit" && !canEditAction) {
      showSnackBar(t("generic_not_allowed") || "Not allowed", "error");
      return;
    }

    const isValid = await methods.trigger();
    if (!isValid) {
      return;
    }

    const formDataValues = methods.getValues();

    setIsSubmitting(true);
    try {
      if (mode === "create") {
        const payload: ActionCreateRequestPayload = {
          name: formDataValues.name,
          code: formDataValues.code,
          description: formDataValues.description,
          type: formDataValues.type,
          status: formDataValues.status,
          link: formDataValues.link,
          order: formDataValues.order,
          icon: formDataValues.icon,
          subActions: formDataValues.subActions,
          createdBy: userInfo?._id || "",
        } as ActionCreateRequestPayload;

        await actionCreateOne.mutateAsync(payload);
        showSnackBar("Action created successfully", "success");

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ACTIONS,
          option: OPTIONS.CREATE,
          actionCode: ACTION_CODE.WEB_ACTIONS_CREATE,
          action: "When creating a new action",
          detail: `Created action ${formDataValues.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id || "",
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      } else {
        const payload: ActionUpdateRequestPayload = {
          ...formDataValues,
          _id: action?._id || "",
          updatedBy: userInfo?._id || "",
        } as ActionUpdateRequestPayload;

        await actionUpdateOne.mutateAsync({
          id: action?._id || "",
          data: payload,
        });
        showSnackBar("Action updated successfully", "success");

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ACTIONS,
          option: OPTIONS.EDIT,
          actionCode: ACTION_CODE.WEB_ACTIONS_EDIT,
          action: "When editing an action",
          detail: `Edited action ${formDataValues.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id || "",
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      }

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const err = error as ErrorResponse;
      const errorMessage = Array.isArray(err.message)
        ? err.message[0]
        : err.message || err.error || "An error occurred";

      showSnackBar(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <ActionModalPresenter
          open={open}
          mode={mode}
          isSubmitting={
            isSubmitting ||
            actionCreateOne.isPending ||
            actionUpdateOne.isPending
          }
          isLoadingActionData={false}
          onClose={onClose}
          onSubmit={handleSubmit}
          allActions={allActions}
          isLoadingAllActions={isLoadingAllActions}
        />
      </FormProvider>

      {/* Permission Denied Modal */}
      <PermissionDeniedModal
        open={isPermissionDeniedModalOpen}
        onClose={() => setIsPermissionDeniedModalOpen(false)}
      />
    </>
  );
};
